import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, getDocs, getDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconPlus, IconArrowUp, IconArrowDown, IconTrash } from '@tabler/icons-react';
import { inputStyle, labelStyle } from '../contracts/contractsApi';
import { call, PACK_STATUS, PARTY_FIELDS, REGISTRIES, KIND_LABEL, Chip, fmt, banner } from './packsApi';

export default function Packs() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => onSnapshot(collection(db, 'signingPacks'), s =>
    setPacks(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))),
  e => setError(e.message)), []);
  const cols = '1.6fr 1fr 130px 120px 120px 120px';
  return (
    <Layout>
      <PageHeader title="Signing packs" subtitle="One link that takes a client through their documents, in order"
        actions={<Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setBuilding(b => !b)}>New pack</Button>} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1150 }}>
        {error && <div style={banner('danger')}>{error}</div>}
        {building && <PackBuilder onCreated={(id) => navigate(`/packs/${id}`)} onCancel={() => setBuilding(false)} />}
        {packs && (
          <Card padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '10px 16px', fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div>Pack</div><div>Client</div><div>Status</div><div>Sent</div><div>Signed</div><div>Paid</div>
            </div>
            {packs.length === 0 && <div style={{ padding: '24px 16px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No packs yet.</div>}
            {packs.map((p, i) => {
              const [label, tone] = PACK_STATUS[p.status] || [p.status, 'none'];
              return (
                <div key={p.id} onClick={() => navigate(`/packs/${p.id}`)} style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '11px 16px', alignItems: 'center', fontSize: 12, cursor: 'pointer', borderBottom: i === packs.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontWeight: 500 }}>{p.title}</div>
                  <div style={{ color: 'var(--color-text-secondary)' }}>{p.clientName}</div>
                  <div><Chip label={label} tone={tone} /></div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{fmt(p.sentAt)}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{fmt(p.completedAt)}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{p.payment ? fmt(p.paidAt) : 'No payment'}</div>
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function PackBuilder({ onCreated, onCancel }) {
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newProject, setNewProject] = useState('');
  const [draftPlan, setDraftPlan] = useState(null);
  const [linkPlan, setLinkPlan] = useState(true);
  const [title, setTitle] = useState('');
  const [signer, setSigner] = useState({ name: '', email: '', role: '' });
  const [docs, setDocs] = useState([]); // template ids, in order
  const [customKeys, setCustomKeys] = useState([]);
  const [custom, setCustom] = useState({});
  const [prefill, setPrefill] = useState({});
  const [payOn, setPayOn] = useState(false);
  const [currency, setCurrency] = useState('gbp');
  const [instalments, setInstalments] = useState([{ amount: '', offset: 0 }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getDocs(collection(db, 'clientProjects')).then(s => setProjects(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.id.localeCompare(a.id))));
    getDocs(query(collection(db, 'contractTemplates'), where('status', '==', 'active'))).then(s => setTemplates(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);
  useEffect(() => {
    setDraftPlan(null);
    if (!projectId) return;
    const p = projects.find(x => x.id === projectId);
    if (p && !title) setTitle(`${p.clientName}: ${p.name}`);
    getDocs(query(collection(db, 'clientProjects', projectId, 'planVersions'), where('status', '==', 'draft'))).then(s => setDraftPlan(s.docs[0] ? { id: s.docs[0].id, ...s.docs[0].data() } : null));
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Custom fields needed by the chosen templates' current versions.
    Promise.all(docs.map(id => { const t = templates.find(x => x.id === id); return getDoc(doc(db, 'contractTemplates', id, 'versions', String(t.currentVersion))); }))
      .then(vs => setCustomKeys([...new Set(vs.flatMap(v => v.data()?.customKeys || []))].sort()));
  }, [docs, templates]);

  const move = (i, d) => setDocs(x => { const n = [...x]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const toMinor = (v) => Math.round(parseFloat(String(v).replace(/[,\s]/g, '')) * 100);
  const create = async () => {
    setBusy(true); setError(null);
    try {
      const r = await call('createPack', {
        projectId: projectId || null, clientName: newClient.trim(), projectName: newProject.trim(), title, signer, documents: docs.map(templateId => ({ templateId })),
        planVersionId: draftPlan && linkPlan ? draftPlan.id : null, custom, partyPrefill: prefill,
        payment: payOn ? { currency, instalments: instalments.map(i => ({ amount: toMinor(i.amount), dueOffsetDays: Number(i.offset) })) } : null,
      });
      onCreated(r.packId);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const sect = { fontSize: 13, fontWeight: 500, margin: '16px 0 8px' };
  return (
    <Card>
      <div style={{ fontSize: 14, marginBottom: 12 }}>New signing pack</div>
      {error && <div style={{ ...banner('danger'), marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <div><label style={labelStyle}>Client project</label>
          <select style={inputStyle} value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">New project (created with this pack)</option>{projects.map(p => <option key={p.id} value={p.id}>{p.clientName} · {p.name}</option>)}
          </select></div>
        <div><label style={labelStyle}>Pack title</label><input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Client: build agreement" /></div>
        {!projectId && <>
          <div><label style={labelStyle}>Client name</label><input style={inputStyle} value={newClient} onChange={e => setNewClient(e.target.value)} placeholder="e.g. The Forestry Company" /></div>
          <div><label style={labelStyle}>Project name (optional, can be set later)</label><input style={inputStyle} value={newProject} onChange={e => setNewProject(e.target.value)} placeholder="Left blank: “Client (untitled project)”" /></div>
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--color-text-tertiary)' }}>A blank project is created with its portal closed. Name it and add the plan later from Client projects.</div>
        </>}
      </div>

      <div style={sect}>Signer (one per pack)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr', gap: 10 }}>
        <input style={inputStyle} placeholder="Full name" value={signer.name} onChange={e => setSigner({ ...signer, name: e.target.value })} />
        <input style={inputStyle} placeholder="Email" type="email" value={signer.email} onChange={e => setSigner({ ...signer, email: e.target.value })} />
        <input style={inputStyle} placeholder="Role, e.g. Director" value={signer.role} onChange={e => setSigner({ ...signer, role: e.target.value })} />
      </div>

      <div style={sect}>Documents, in signing order</div>
      {docs.map((id, i) => {
        const t = templates.find(x => x.id === id);
        return (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0' }}>
            <span style={{ width: 18, color: 'var(--color-text-tertiary)' }}>{i + 1}.</span>
            <span style={{ flex: 1 }}>{t?.name} <span style={{ color: 'var(--color-text-tertiary)' }}>· {KIND_LABEL[t?.kind]} · v{t?.currentVersion}</span></span>
            <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} icon={<IconArrowUp size={12} stroke={1.5} />} />
            <Button size="sm" variant="ghost" disabled={i === docs.length - 1} onClick={() => move(i, 1)} icon={<IconArrowDown size={12} stroke={1.5} />} />
            <Button size="sm" variant="ghost" onClick={() => setDocs(docs.filter(x => x !== id))} icon={<IconTrash size={12} stroke={1.5} />} />
          </div>
        );
      })}
      <select style={{ ...inputStyle, width: 320 }} value="" onChange={e => e.target.value && setDocs([...docs, e.target.value])}>
        <option value="">Add a template…</option>
        {templates.filter(t => !docs.includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.name} (v{t.currentVersion})</option>)}
      </select>
      {draftPlan && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginTop: 10 }}>
          <input type="checkbox" checked={linkPlan} onChange={e => setLinkPlan(e.target.checked)} />
          Add the draft build plan as the last document, "Schedule A" ({draftPlan.counts?.tasks} tasks · {draftPlan.counts?.milestones} milestones · {draftPlan.counts?.workstreams} workstreams). It becomes the baseline when signed.
        </label>
      )}

      {customKeys.length > 0 && <>
        <div style={sect}>Pack values</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {customKeys.map(k => (
            <div key={k}><label style={labelStyle}><span style={{ fontFamily: 'var(--font-mono)' }}>{`{{pack.custom.${k}}}`}</span></label>
              <input style={inputStyle} value={custom[k] || ''} onChange={e => setCustom({ ...custom, [k]: e.target.value })} /></div>
          ))}
        </div>
      </>}

      <div style={sect}>Client details you already know (optional; the client confirms them but can't change them)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {PARTY_FIELDS.filter(([k]) => !['signerEmail', 'signerName'].includes(k)).map(([k, l]) => (
          k === 'registry'
            ? <select key={k} style={inputStyle} value={prefill.registry || ''} onChange={e => setPrefill({ ...prefill, registry: e.target.value || undefined })}><option value="">Registry: client chooses</option>{REGISTRIES.map(r => <option key={r}>{r}</option>)}</select>
            : <input key={k} style={inputStyle} placeholder={l} value={prefill[k] || ''} onChange={e => setPrefill({ ...prefill, [k]: e.target.value })} />
        ))}
      </div>

      <div style={sect}>Payment</div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 8 }}>
        <input type="checkbox" checked={payOn} onChange={e => setPayOn(e.target.checked)} /> Take payment after signing (no VAT or tax lines)
      </label>
      {payOn && (
        <div>
          <select style={{ ...inputStyle, width: 120, marginBottom: 8 }} value={currency} onChange={e => setCurrency(e.target.value)}>
            <option value="gbp">GBP</option><option value="eur">EUR</option><option value="usd">USD</option>
          </select>
          {instalments.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
              <span style={{ width: 90 }}>Instalment {i + 1}</span>
              <input style={{ ...inputStyle, width: 140 }} placeholder="Amount, e.g. 5000" value={it.amount} onChange={e => setInstalments(instalments.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
              {i === 0 ? <span style={{ color: 'var(--color-text-tertiary)' }}>paid at signing</span> : <>
                <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={it.offset} onChange={e => setInstalments(instalments.map((x, j) => (j === i ? { ...x, offset: e.target.value } : x)))} />
                <span style={{ color: 'var(--color-text-tertiary)' }}>days after signing</span>
                <Button size="sm" variant="ghost" onClick={() => setInstalments(instalments.filter((_, j) => j !== i))} icon={<IconTrash size={12} stroke={1.5} />} />
              </>}
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={() => setInstalments([...instalments, { amount: '', offset: 30 * instalments.length }])}>Add instalment</Button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <Button loading={busy} disabled={(!projectId && !newClient.trim()) || !docs.length || !signer.email || !signer.name} onClick={create}>Create draft</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}
